import { ThemeProvider } from "@emotion/react";
import { CssBaseline, ScopedCssBaseline } from "@mui/material";
// import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import {
	QueryCache,
	QueryClient,
	QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
// import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { useEffect, useState } from "react";
import { RouterProvider } from "react-router-dom";
import { Toaster } from "sonner";
import { z } from "zod";

import { DocumentExtractModal } from "AdvisoryManagement/Shared/DocumentExtract/DocumentExtractModal";
import { processSignOut } from "Auth/Hooks/useSignOut";
import { JWT_KEY } from "Auth/Models/AuthConstants";
import type { FetchResponse } from "Infrastructure/Api/Api";
import { getJwtKey, setupClient } from "Infrastructure/Api/Api";
import { handleResponseError } from "Infrastructure/Api/ApiUtils";
import { getTheme } from "Infrastructure/Themes/Themes";
import { initI18n } from "Infrastructure/Translations";
import { initSentry } from "Infrastructure/Utils/SentryUtils";
import { trackUserIdentification } from "Infrastructure/Utils/TrackingUtils";
import { appRouter } from "./Infrastructure/Routing/AppRouter";

// const persister = createAsyncStoragePersister({
// 	storage: window.localStorage,
// });

function App() {
	const [i18nextLoaded, setI18nextLoaded] = useState(false);

	initSentry();

	useEffect(() => {
		initI18n(() => setI18nextLoaded(true));
	}, []);

	const theme = getTheme();

	z.config(z.locales.cs());

	const responseMiddleware = {
		name: "auth-middleware",
		fn: <TData, TStatus extends number>(
			response: FetchResponse<TData, TStatus>,
		): FetchResponse<TData, TStatus> => {
			const token = getJwtKey();
			if (token !== undefined && token !== null && response.status === 401) {
				processSignOut(queryClient);
			} else {
				trackUserIdentification();
			}
			return response;
		},
	};

	setupClient({
		apiUrl: `${import.meta.env.VITE_API_URL}`,
		jwtKey: JWT_KEY,
		requestMiddlewares: [],
		responseMiddlewares: [responseMiddleware],
	});

	const queryClient = new QueryClient({
		defaultOptions: {
			mutations: {
				onError: handleResponseError,
			},
			queries: {
				gcTime: Number.POSITIVE_INFINITY,
				staleTime: Number.POSITIVE_INFINITY,
				retry: false,
			},
		},
		queryCache: new QueryCache({
			onError: handleResponseError,
		}),
	});

	if (!i18nextLoaded) {
		return null;
	}

	return (
		<QueryClientProvider
			client={queryClient}
			// persistOptions={{ persister }}
		>
			<ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<Toaster richColors position="bottom-center" />
				<DocumentExtractModal />
				<ScopedCssBaseline>
					<RouterProvider router={appRouter} />
				</ScopedCssBaseline>
			</ThemeProvider>
		</QueryClientProvider>
	);
}
export default App;
